import styled, { css, keyframes } from "styled-components";
import { Theme } from "./Theme";

type ButtonVariant = "accent" | "default" | "muted";

type StyledButtonPropsType = {
  width?: string;
  height?: string;
  margin?: string;
  radius?: string;
  zIndex?: number;
  active?: boolean;
  fullWidth?: boolean;
  variant?: ButtonVariant;
};

const buttonShimmer = keyframes`
  0% {
    transform: translateX(-180%) skewX(-20deg);
    opacity: 0;
  }
  20% {
    opacity: 0.22;
  }
  35% {
    opacity: 0.42;
  }
  50% {
    opacity: 0.20;
  }
  100% {
    transform: translateX(260%) skewX(-20deg);
    opacity: 0;
  }
`;

const accentPulse = keyframes`
  0% {
    box-shadow:
      inset 0 1px 0 rgba(255,255,255,0.24),
      inset 0 -10px 24px rgba(255,255,255,0.05),
      0 8px 20px rgba(106, 217, 255, 0.16),
      0 0 0 1px rgba(151, 239, 255, 0.10),
      0 0 18px rgba(106, 217, 255, 0.10),
      0 0 34px rgba(106, 217, 255, 0.06);
    filter: brightness(1);
  }

  50% {
    box-shadow:
      inset 0 1px 0 rgba(255,255,255,0.30),
      inset 0 -12px 28px rgba(255,255,255,0.08),
      0 10px 24px rgba(106, 217, 255, 0.22),
      0 0 0 1px rgba(151, 239, 255, 0.16),
      0 0 24px rgba(106, 217, 255, 0.16),
      0 0 48px rgba(106, 217, 255, 0.12);
    filter: brightness(1.03);
  }

  100% {
    box-shadow:
      inset 0 1px 0 rgba(255,255,255,0.24),
      inset 0 -10px 24px rgba(255,255,255,0.05),
      0 8px 20px rgba(106, 217, 255, 0.16),
      0 0 0 1px rgba(151, 239, 255, 0.10),
      0 0 18px rgba(106, 217, 255, 0.10),
      0 0 34px rgba(106, 217, 255, 0.06);
    filter: brightness(1);
  }
`;

const getVariantStyles = (variant: ButtonVariant = "default") => {
  if (variant === "accent") {
    return css`
      color: #041018;
      border: 1px solid rgba(160, 241, 255, 0.34);

      background:
        radial-gradient(
          circle at 50% 0%,
          rgba(255, 255, 255, 0.30) 0%,
          rgba(255, 255, 255, 0.11) 24%,
          rgba(255, 255, 255, 0) 58%
        ),
        radial-gradient(
          circle at 50% 120%,
          rgba(255, 255, 255, 0.10) 0%,
          rgba(255, 255, 255, 0) 52%
        ),
        linear-gradient(
          180deg,
          rgba(162, 242, 255, 0.98) 0%,
          rgba(106, 217, 255, 0.97) 46%,
          rgba(56, 186, 226, 0.94) 100%
        );

      box-shadow:
        inset 0 1px 0 rgba(255,255,255,0.24),
        inset 0 -10px 24px rgba(255,255,255,0.05),
        0 8px 20px rgba(106, 217, 255, 0.18),
        0 0 0 1px rgba(151, 239, 255, 0.12),
        0 0 20px rgba(106, 217, 255, 0.12),
        0 0 36px rgba(106, 217, 255, 0.07);

      &:hover {
        border-color: rgba(176, 245, 255, 0.42);
        box-shadow:
          inset 0 1px 0 rgba(255,255,255,0.30),
          inset 0 -12px 28px rgba(255,255,255,0.07),
          0 10px 24px rgba(106, 217, 255, 0.22),
          0 0 0 1px rgba(151, 239, 255, 0.16),
          0 0 24px rgba(106, 217, 255, 0.16),
          0 0 44px rgba(106, 217, 255, 0.10);
      }
    `;
  }

  if (variant === "muted") {
    return css`
      color: rgba(255,255,255,0.88);
      border: 1px solid rgba(255, 255, 255, 0.07);
      background:
        radial-gradient(
          circle at 50% 0%,
          rgba(255, 255, 255, 0.06) 0%,
          rgba(255, 255, 255, 0.02) 24%,
          rgba(255, 255, 255, 0) 58%
        ),
        linear-gradient(
          180deg,
          rgba(255,255,255,0.026) 0%,
          rgba(255,255,255,0.012) 100%
        ),
        linear-gradient(
          195deg,
          #111111 0%,
          #0b0b0b 100%
        );

      box-shadow:
        inset 0 1px 0 rgba(255,255,255,0.04),
        inset 0 -8px 18px rgba(255,255,255,0.01),
        0 8px 20px rgba(0,0,0,0.16);

      &:hover {
        border-color: rgba(255,255,255,0.10);
        box-shadow:
          inset 0 1px 0 rgba(255,255,255,0.05),
          inset 0 -8px 18px rgba(255,255,255,0.02),
          0 10px 24px rgba(0,0,0,0.20);
      }
    `;
  }

  return css`
    color: ${Theme.colors.mainFontColor};
    border: 1px solid rgba(106, 217, 255, 0.12);
    background:
      radial-gradient(
        circle at 50% 0%,
        rgba(106, 217, 255, 0.10) 0%,
        rgba(106, 217, 255, 0.03) 24%,
        rgba(106, 217, 255, 0) 58%
      ),
      linear-gradient(
        180deg,
        rgba(255,255,255,0.028) 0%,
        rgba(255,255,255,0.012) 100%
      ),
      linear-gradient(
        195deg,
        #111111 0%,
        ${Theme.colors.blockColor} 100%
      );

    box-shadow:
      inset 0 1px 0 rgba(255,255,255,0.05),
      inset 0 -10px 22px rgba(106, 217, 255, 0.015),
      0 8px 22px rgba(0,0,0,0.18);

    &:hover {
      border-color: rgba(106, 217, 255, 0.18);
      box-shadow:
        inset 0 1px 0 rgba(255,255,255,0.06),
        inset 0 -10px 22px rgba(106, 217, 255, 0.02),
        0 10px 26px rgba(0,0,0,0.20),
        0 0 18px rgba(106, 217, 255, 0.05);
    }
  `;
};

export const StyledButton = styled.button<StyledButtonPropsType>`
  position: relative;
  overflow: hidden;
  isolation: isolate;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
  width: ${({ width, fullWidth }) => (fullWidth ? "100%" : width || "100%")};
  min-height: ${({ height }) => height || "52px"};
  padding: 0 18px;
  margin: ${({ margin }) => margin || "0"};
  border-radius: ${({ radius }) => radius || "16px"};
  z-index: ${({ zIndex }) => zIndex || 1};

  cursor: pointer;
  user-select: none;
  -webkit-tap-highlight-color: transparent;
  outline: none;

  font-family: "Manrope", sans-serif;
  font-size: ${Theme.font.fontSize3};
  font-weight: ${Theme.font.fontWeight2};
  letter-spacing: 0.02em;
  line-height: 1;
  text-align: center;
  white-space: nowrap;
  text-decoration: none;

  transform: translateZ(0);
  transition:
    transform 0.16s ease,
    border-color 0.18s ease,
    box-shadow 0.18s ease,
    opacity 0.18s ease,
    filter 0.18s ease,
    background 0.18s ease;

  ${({ variant }) => getVariantStyles(variant)}

  & > * {
    position: relative;
    z-index: 2;
  }

  &::before {
    content: "";
    position: absolute;
    inset: 1px;
    border-radius: inherit;
    pointer-events: none;
    background:
      linear-gradient(
        180deg,
        rgba(255,255,255,0.12) 0%,
        rgba(255,255,255,0.04) 16%,
        rgba(255,255,255,0) 50%
      );
    opacity: 0.9;
    z-index: 0;
  }

  &::after {
    content: "";
    position: absolute;
    top: -20%;
    left: -25%;
    width: 36%;
    height: 180%;
    pointer-events: none;
    border-radius: 999px;
    background: linear-gradient(
      90deg,
      rgba(255,255,255,0) 0%,
      rgba(255,255,255,0.18) 38%,
      rgba(255,255,255,0.55) 50%,
      rgba(255,255,255,0.18) 62%,
      rgba(255,255,255,0) 100%
    );
    opacity: 0;
    filter: blur(4px);
    transform: translateX(-180%) skewX(-20deg);
    will-change: transform, opacity;
    z-index: 1;
  }

  ${({ active, variant }) =>
    active &&
    variant === "accent" &&
    css`
      animation: ${accentPulse} 2.8s ease-in-out infinite;

      &::after {
        animation: ${buttonShimmer} 2.2s ease-in-out infinite;
      }
    `}

  ${({ active, variant }) =>
    active &&
    variant !== "accent" &&
    css`
      box-shadow:
        inset 0 1px 0 rgba(255,255,255,0.06),
        inset 0 -10px 22px rgba(106, 217, 255, 0.02),
        0 10px 26px rgba(0,0,0,0.20),
        0 0 0 3px rgba(106, 217, 255, 0.06),
        0 0 22px rgba(106, 217, 255, 0.08);
    `}

  &:active {
    transform: translateY(1px) scale(0.988);
  }

  &:disabled {
    cursor: default;
    opacity: 0.46;
    transform: none;
    animation: none;
    filter: grayscale(0.08);

    &::after {
      animation: none;
    }
  }

  &:focus-visible {
    box-shadow:
      0 0 0 3px rgba(106, 217, 255, 0.10),
      0 0 0 1px rgba(106, 217, 255, 0.22);
  }
`;
